import Autosuggest, {
  RenderSuggestionsContainerParams,
  Theme as ReactAutoSuggestTheme,
} from 'react-autosuggest';
import React, { useState } from 'react';
import { AutoSuggestInput } from './AutoSuggestInput';
import { defineMessages, useIntl } from 'react-intl';
import { ListItem, ListItemText, useControlled } from '@mui/material';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { useCustomCompareEffect } from 'react-use';
import { Paper } from '@lifeomic/chroma-react/components/Paper';
import { TextFieldProps } from '@lifeomic/chroma-react/components/TextField';
import clsx from 'clsx';
import { getTestProps } from '../../util/testProps';
import { alpha } from '@mui/material/styles';

const CONTAINER_HEIGHT = 300;

const messages = defineMessages({
  noResults: {
    id: 'newSelect.suggestions.noResults',
    defaultMessage: 'No results.',
  },
});

const useStyles = makeStyles(theme => ({
  container: {
    position: 'static',
    width: '100%',
  },
  suggestionsContainer: {
    marginTop: theme.spacing(0.5),
    maxHeight: theme.pxToRem(CONTAINER_HEIGHT),
    maxWidth: '17.25rem',
    overflow: 'auto',
    position: 'absolute',
    width: '100%',
    zIndex: 1,
  },
  suggestionsList: {
    listStyleType: 'none',
    margin: 0,
    padding: 0,
  },
  suggestion: {
    display: 'flex',
    alignItems: 'center',
    transition: 'background 0.25s ease, opacity 0.25s ease',
    '&:hover, &:focus': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.blue.main, 0.2)
          : alpha(theme.palette.blue[100], 0.6),
      cursor: 'pointer',
    },
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  noResultsPrimary: {
    color: theme.palette.text.primary,
  },
  noResultsSecondary: {
    color: theme.palette.text.secondary,
  },
  suggestionContainerRoot: {
    position: 'absolute',
    width: '93%',
    zIndex: 20,
  },
}));

export interface ContainerProps {
  id: string;
  key: string;
  className: string;
  ref: any;
  role: string;
}

export interface InputProps {
  onChange(
    event: any,
    params: {
      newValue: string;
      method: 'down' | 'up' | 'escape' | 'enter' | 'click' | 'type';
    },
  ): void;
  onBlur?(event: any, params?: { highlightedSuggestion: any }): void;
  value: string;
  [key: string]: any;
}

export interface AutoSuggestTestIds {
  input?: string;
  noResults?: string;
}

export type AutoSuggestTheme = ReactAutoSuggestTheme;

export type NoResultsMessage = {
  primary?: string;
  secondary?: string;
};

export interface AutoSuggestProps {
  alwaysRenderSuggestions?: boolean;
  autoSuggestTheme?: AutoSuggestTheme;
  clearSuggestions?: () => void;
  disabled?: boolean;
  fetchSuggestions: (value: string) => void;
  focusInputOnSuggestionClick?: boolean;
  getSuggestionValue: (term: any) => string;
  inputErrorMessage?: string;
  inputHelperText?: string;
  inputLabel?: string;
  inputPlaceholder?: string;
  inputProps?: TextFieldProps;
  isLoading: boolean;
  onBlur?: (e: any) => void;
  onFocus?: () => void;
  onKeyDown?: (e: any, inputValue: string) => void;
  onSuggestionSelected: (suggestion: any) => void;
  renderSuggestion: (term: any, isHighlighted: boolean) => React.ReactNode;
  shouldHighlightFirstSuggestion?: boolean;
  suggestions: Array<any>;
  suppressNoResultMessage?: boolean;
  testIds?: AutoSuggestTestIds;
  fixedPopup?: boolean;
  persistPopupOnFocusChange?: boolean;
  noResultsMessage?: NoResultsMessage;
  value?: string;
  onChange?: InputProps['onChange'];
  clearOnSubmit?: boolean;
  onlyShowWhenFocused?: boolean;
  autoFocus?: boolean;
}

const getPosition = (ref: any) => {
  const rect =
    (ref?.current?.input as HTMLInputElement)?.getBoundingClientRect?.() ||
    ({} as DOMRect);

  return {
    left: rect.left,
    top: rect.top,
    bottom: rect.bottom,
  };
};

export const AutoSuggest: React.FC<AutoSuggestProps> = ({
  autoSuggestTheme,
  alwaysRenderSuggestions,
  clearSuggestions,
  disabled,
  fetchSuggestions,
  focusInputOnSuggestionClick,
  getSuggestionValue,
  inputErrorMessage = '',
  inputHelperText = '',
  inputLabel,
  inputPlaceholder,
  inputProps: textFieldInputProps,
  isLoading,
  onBlur,
  onFocus,
  onKeyDown,
  onSuggestionSelected,
  renderSuggestion,
  shouldHighlightFirstSuggestion = true,
  suggestions = [],
  suppressNoResultMessage = true, // suppress no result message on focus
  testIds,
  fixedPopup,
  persistPopupOnFocusChange,
  noResultsMessage,
  value,
  onChange,
  clearOnSubmit = true,
  onlyShowWhenFocused = false,
  autoFocus = false,
}) => {
  const inputRef = React.useRef(null);
  const [inputValue, setInputValue] = useControlled<string>({
    controlled: value,
    default: '',
    name: 'AutoSuggest.inputValue',
  });
  const [isFocused, setIsFocused] = React.useState(false);
  const classes = useStyles({});
  // break these up to avoid rerenders
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  const [top, setTop] = useState<number>(null);
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  const [left, setLeft] = useState<number>(null);
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  const [bottom, setBottom] = useState<number>(null);
  const { formatMessage } = useIntl();

  // poll for a position change for fixed suggesiton container
  useCustomCompareEffect(
    () => {
      if (!fixedPopup) return;

      const interval = setInterval(() => {
        const { left, top, bottom } = getPosition(inputRef);
        setTop(top);
        setLeft(left);
        setBottom(bottom);
      }, 250);

      return () => {
        clearInterval(interval);
      };
    },
    [fixedPopup, setTop, setLeft, setBottom],
    ([oldFixedPopup], [newFixedPopup]) => oldFixedPopup === newFixedPopup,
  );

  const maybeSetInputPosition = () => {
    if (fixedPopup) {
      const { left, top, bottom } = getPosition(inputRef);
      setTop(top);
      setLeft(left);
      setBottom(bottom);
    }
  };

  const changeHandler = onChange ? onChange : setInputValue;

  const onInputChange: InputProps['onChange'] = (
    _event: any,
    { newValue, method },
  ): void => {
    if (inputValue !== newValue) {
      changeHandler(newValue, { method, newValue });
    }
  };

  const handleClearSuggestions = () => {
    clearSuggestions && clearSuggestions();
  };

  const handleFetchSuggestions = ({ value }: { value: any }) => {
    fetchSuggestions && fetchSuggestions(value);
  };

  const handleGetSuggestionValue = (term: any) => {
    return getSuggestionValue && getSuggestionValue(term);
  };

  const handleOnBlur = (e: any) => {
    !persistPopupOnFocusChange &&
      changeHandler('', { method: 'escape', newValue: '' });
    setIsFocused(false);
    onBlur?.(e);
  };

  const handleOnFocus = () => {
    maybeSetInputPosition();
    setIsFocused(true);
    onFocus && onFocus();
  };

  const handleOnKeyDown = (e: any) => {
    // We need to check if we need to toggle our focus state here.
    // This ensures that when an item is selected (via ENTER key) *and*
    // the input still has focus, that if the user begins typing again,
    // they can continue to select items
    if (!isFocused) {
      maybeSetInputPosition();
      setIsFocused(true);
    }

    onKeyDown && onKeyDown(e, inputValue);
  };

  const handleOnSuggestionSelected = (_e: any, suggestion: any) => {
    clearOnSubmit && changeHandler('', { method: 'enter', newValue: '' });
    setIsFocused(false);
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    inputRef.current.input.blur();
    onSuggestionSelected && onSuggestionSelected(suggestion);
  };

  const handleRenderSuggestion = (
    term: any,
    { isHighlighted }: { isHighlighted: boolean },
  ) => {
    return renderSuggestion && renderSuggestion(term, isHighlighted);
  };

  const renderInputComponent = (inputProps: InputProps) => {
    const { ref } = inputProps;

    return (
      <AutoSuggestInput
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        disabled={disabled}
        errorMessage={inputErrorMessage}
        fullWidth
        helperText={inputHelperText}
        inputProps={{
          inputRef: (node: any) => {
            ref(node);
          },
          ...inputProps,
          ...getTestProps(testIds?.input),
        }}
        isLoading={isLoading}
        label={inputLabel}
        placeholder={inputPlaceholder}
      />
    );
  };

  const renderSuggestionsContainer = ({
    children,
    containerProps,
  }: RenderSuggestionsContainerParams) => {
    // TODO: remove this callRef and just pass ref directly once chroma updates
    const { className, ...toPass } = containerProps;

    const showNoResults =
      (!suppressNoResultMessage || inputValue) &&
      !isLoading &&
      !suggestions.length;

    const callRef = (node: any) => {
      if (node !== null) {
        containerProps.ref(node);
      }
    };

    let style = (toPass as any)?.style || {};
    if (fixedPopup && (top || bottom)) {
      const placeOnTop = window.innerHeight - bottom < 300;
      style = {
        ...style,
        position: 'fixed',
        left: left,
        ...(placeOnTop
          ? {
              bottom: window.innerHeight - top,
            }
          : { top: bottom }),
      };
    }

    const paper = (
      <Paper
        className={clsx(className, classes.suggestionContainerRoot)}
        padding={0}
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        ref={callRef(containerProps.ref) as any}
        {...toPass}
        style={style}
      >
        {children}
        {showNoResults && (
          <ListItem {...getTestProps(testIds?.noResults)}>
            <ListItemText
              primary={
                noResultsMessage?.primary || formatMessage(messages.noResults)
              }
              secondary={noResultsMessage?.secondary}
              primaryTypographyProps={{ className: classes.noResultsPrimary }}
              secondaryTypographyProps={{
                className: classes.noResultsSecondary,
              }}
            />
          </ListItem>
        )}
      </Paper>
    );

    return paper;
  };

  return (
    <Autosuggest
      alwaysRenderSuggestions={alwaysRenderSuggestions}
      focusInputOnSuggestionClick={focusInputOnSuggestionClick}
      getSuggestionValue={handleGetSuggestionValue}
      highlightFirstSuggestion={shouldHighlightFirstSuggestion}
      onSuggestionHighlighted={() => void 0}
      inputProps={{
        ...textFieldInputProps,
        onBlur: handleOnBlur,
        onChange: onInputChange,
        onFocus: handleOnFocus,
        onKeyDown: handleOnKeyDown,
        value: inputValue,
      }}
      onSuggestionsClearRequested={handleClearSuggestions}
      onSuggestionsFetchRequested={handleFetchSuggestions}
      onSuggestionSelected={handleOnSuggestionSelected}
      renderInputComponent={renderInputComponent}
      renderSuggestion={handleRenderSuggestion}
      renderSuggestionsContainer={renderSuggestionsContainer}
      shouldRenderSuggestions={() => (onlyShowWhenFocused ? isFocused : true)}
      suggestions={suggestions}
      ref={inputRef}
      theme={{
        ...(autoSuggestTheme || {}),
        container: clsx(classes.container, autoSuggestTheme?.container),
        suggestionsContainer: clsx(
          classes.suggestionsContainer,
          autoSuggestTheme?.suggestionsContainer,
        ),
        suggestionsList: clsx(
          classes.suggestionsList,
          autoSuggestTheme?.suggestionsList,
        ),
        suggestion: clsx(classes.suggestion, autoSuggestTheme?.suggestion),
      }}
    />
  );
};
